// src/services/firebase.js — DR-Aura Smart Recipe Finder

import { initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  getDocs
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { getRemoteConfig, fetchAndActivate } from 'firebase/remote-config';

// --- Firebase Init ---
const firebaseConfig = {
  apiKey: "AIzaSyBhOw_L5lGuMwOQYWkyKpg2jcbETvVG28E",
  authDomain: "dr-aura-smart-recipe-finder2.firebaseapp.com",
  projectId: "dr-aura-smart-recipe-finder2",
  storageBucket: "dr-aura-smart-recipe-finder2.appspot.com",
  messagingSenderId: "414299584596",
  appId: "1:414299584596:web:e75ce74edc5484efab55c1",
  measurementId: "G-GHSXY12JPT"
};

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const analytics = getAnalytics(app);
export const remoteConfig = getRemoteConfig(app);

export const googleProvider = new GoogleAuthProvider();

// --- Auth Helpers ---
export const signInWithGoogle = async () => {
  const result = await signInWithPopup(auth, googleProvider);
  return result.user ?? null;
};

export const signInEmail = async (email, password) => {
  const result = await signInWithEmailAndPassword(auth, email, password);
  return result.user ?? null;
};

export const signUpEmail = async (email, password, displayName) => {
  const result = await createUserWithEmailAndPassword(auth, email, password);
  if (displayName) {
    await updateProfile(result.user, { displayName });
  }
  return result.user ?? null;
};

export const updateUserProfile = async (user, profileData) => {
  if (!user) throw new Error("No user provided to update profile");
  await updateProfile(user, profileData);
};

export const logOut = async () => {
  await signOut(auth);
};

export const resetPassword = async (email) => {
  await sendPasswordResetEmail(auth, email);
};

export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// --- Analytics ---
export const trackEvent = (eventName, params) => {
  try {
    logEvent(analytics, eventName, params);
  } catch {
    // swallow errors
  }
};

// --- Firestore User Helpers ---
export const getUserDoc = async (uid) => {
  if (!uid) return null;
  const ref = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
};

export const createUserDoc = async (uid, data) => {
  if (!uid) throw new Error("createUserDoc: UID is required");
  const ref = doc(db, 'users', uid);
  await setDoc(ref, {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const updateUserDoc = async (uid, data) => {
  if (!uid) throw new Error("updateUserDoc: UID is required");
  const ref = doc(db, 'users', uid);
  await setDoc(ref, { ...data, updatedAt: serverTimestamp() }, { merge: true });
};

// --- Firestore Recipe Helpers ---
export const saveRecipe = async (uid, recipeData) => {
  if (!uid) throw new Error("saveRecipe: UID is required");
  const ref = doc(db, 'users', uid);
  await updateDoc(ref, {
    savedRecipes: arrayUnion(recipeData),
    updatedAt: serverTimestamp()
  });
};

export const unsaveRecipe = async (uid, recipeData) => {
  if (!uid) throw new Error("unsaveRecipe: UID is required");
  const ref = doc(db, 'users', uid);
  await updateDoc(ref, {
    savedRecipes: arrayRemove(recipeData),
    updatedAt: serverTimestamp()
  });
};

export const getSavedRecipes = async (uid) => {
  if (!uid) return [];
  const ref = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data().savedRecipes || [] : [];
};

// --- Firestore Community Recipes ---
export const onCommunityRecipesUpdate = (callback) => {
  const q = query(collection(db, 'communityRecipes'), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const recipes = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    callback(recipes);
  });
};

export const uploadCommunityRecipe = async (recipeData) => {
  await addDoc(collection(db, 'communityRecipes'), {
    ...recipeData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

// --- Firestore Meal Plan Helpers ---
export const getMealPlan = async (uid) => {
  if (!uid) return null;
  const ref = doc(db, 'mealPlans', uid);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
};

export const saveMealPlan = async (uid, mealPlanData) => {
  if (!uid) throw new Error("saveMealPlan: UID is required");
  const ref = doc(db, 'mealPlans', uid);
  await setDoc(ref, { ...mealPlanData, updatedAt: serverTimestamp() }, { merge: true });
};

// --- Firestore Grocery List Helpers ---
export const getGroceryList = async (uid) => {
  if (!uid) return null;
  const ref = doc(db, 'groceryLists', uid);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
};

export const saveGroceryList = async (uid, groceryData) => {
  if (!uid) throw new Error("saveGroceryList: UID is required");
  const ref = doc(db, 'groceryLists', uid);
  await setDoc(ref, { ...groceryData, updatedAt: serverTimestamp() }, { merge: true });
};

// --- Firestore Recipe Reviews ---
export const getRecipeReviews = async (recipeId) => {
  if (!recipeId) return [];
  const q = query(collection(db, 'recipes', recipeId, 'reviews'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

export const addReview = async (recipeId, reviewData) => {
  if (!recipeId) throw new Error("addReview: recipeId is required");
  await addDoc(collection(db, 'recipes', recipeId, 'reviews'), {
    ...reviewData,
    createdAt: serverTimestamp()
  });
};